"use client";

// app/error.tsx
// Segment-level error boundary — catches unhandled errors in any route
// segment. Can't catch errors thrown in the root layout itself; that's
// what app/global-error.tsx is for.

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[ErrorBoundary]", error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="min-h-screen bg-content-bg flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <p className="text-5xl font-bold text-forge-urgency mb-4 font-mono">
          Error
        </p>
        <h1 className="text-xl font-semibold text-content-text mb-3">
          Something went wrong
        </h1>
        <p className="text-sm text-content-muted mb-8 leading-relaxed">
          An unexpected error occurred. Please try again or return to the
          homepage.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={reset}
            className="rounded-lg border border-content-border px-6 py-3 text-sm font-medium text-content-text transition-colors hover:border-content-muted"
          >
            Try Again
          </button>
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            href="/"
            className="rounded-lg bg-forge-trust px-6 py-3 text-sm font-semibold text-content-bg transition-colors hover:bg-forge-trust-hover"
          >
            Go Home
          </a>
        </div>
        {error.digest && (
          <p className="mt-6 text-xs text-content-muted font-mono">
            Error ID: {error.digest}
          </p>
        )}
      </div>
    </div>
  );
}
